<script lang="ts">
	import type { BadgeColor, BadgeSize, BadgeStyle } from '$lib/components/atoms/badge/types';
	import type { IconPosition } from '$lib/components/atoms/iconlabel/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import IconLabel from '$lib/components/atoms/iconlabel/IconLabel.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import { getStyle } from '$lib/utils/getStyle.js';

	interface Props {
		color?: BadgeColor;
		size?: BadgeSize;
		text?: string;
		icon?: IconName;
		isSquare?: boolean;
		hideTextOnMobile?: boolean;
		iconPosition?: IconPosition;
		amountOfRepeatedIcons?: number;
		colorClasses?: string;
		iconClasses?: string;
		labelClasses?: string;
		imgUrl?: string;
		colorStyles?: BadgeStyle;
		class?: ClassValue;
		children?: Snippet;
	}

	const {
		color = 'primary',
		size = 'md',
		text,
		icon,
		isSquare = false,
		hideTextOnMobile = false,
		iconPosition = 'left',
		amountOfRepeatedIcons,
		colorClasses,
		iconClasses,
		labelClasses,
		imgUrl,
		colorStyles,
		class: classes,
		children,
	}: Props = $props();

	const badgeColorClasses: Record<BadgeColor, string> = {
		primary: 'bg-coloredneutral text-coloredneutral-weakest',
		inverted: 'bg-neutral-soft text-neutral',
		ghost: 'bg-transparent text-coloredneutral',
		hotel: 'bg-hotel text-neutral-weakest',
		hotelInverted: 'bg-hotel-weakest text-hotel-strongest',
		hotelAlt: 'bg-hotel-weak text-coloredneutral',
		hotelMichelin: 'bg-hotel-michelin text-neutral-weakest',
		restaurant: 'bg-restaurant text-neutral-weakest',
		restaurantInverted: 'bg-restaurant-weakest text-restaurant-strongest',
		restaurantAlt: 'bg-restaurant-weak text-coloredneutral',
		restaurantMichelin: 'bg-restaurant-michelin text-neutral-weakest',
		tourism: 'bg-tourism text-neutral-weakest',
		tourismInverted: 'bg-tourism-weakest text-tourism-strongest',
		energy: 'bg-fuel text-neutral-weakest',
		energyInverted: 'bg-fuel-weakest text-fuel-strongest',
		electric: 'bg-electric text-neutral-weakest',
		electricInverted: 'bg-electric-weakest text-electric-strongest',
		parking: 'bg-parking text-neutral-weakest',
		parkingInverted: 'bg-parking-weakest text-parking-strongest',
		step: 'bg-coloredneutral-weakest text-coloredneutral',
		quickest: 'bg-quick-weakest text-coloredneutral',
		economic: 'bg-economic-weakest text-coloredneutral',
		ecological: 'bg-ecologic-weakest text-coloredneutral',
		alternative: 'bg-alternative-weakest text-coloredneutral',
		discovery: 'bg-discovery-weakest text-coloredneutral',
		connector: 'bg-electric-weakest text-black',
		neutral: 'bg-neutral-weak text-coloredneutral',
		zoi: 'bg-primary-weakest text-primary',
		rest: 'bg-rest-stop text-neutral-weakest',
		restSponsored: 'bg-rest-stop-strong text-neutral-weakest',
		restInverted: 'bg-rest-stop-weak text-rest-stop',
		error: 'bg-error text-neutral-weakest',
		warning: 'bg-warning text-neutral-weakest',
		warningAlt: 'bg-warning-weak text-warning',
		gyg: 'bg-neutral-weakest text-gyg',
		car: 'bg-coloredneutral text-neutral-weakest',
		carInverted: 'bg-neutral-soft text-coloredneutral',
		magazine: 'bg-coloredneutral-soft text-neutral-weakest',
		shortest: 'bg-shortest-weakest text-coloredneutral',
		carAction: 'bg-coloredneutral-weakest text-coloredneutral-soft',
	};

	const derivedClasses = $derived(!colorStyles && (colorClasses ?? (color && badgeColorClasses[color])));

	const hasIconOnly = $derived(!!icon && !text);
	const hasImageOnly = $derived(!!imgUrl && !text);

	// Badges height is defined by line-height (included in text-sm and text-md) + padding
	// If text size or padding is updated, it is mandatory to check that every use of Badge is still fine
	const iconContainerDimensionClasses = {
		sm: 'gap-0.5 text-sm h-6 min-w-6',
		md: 'gap-1 text-md h-7 min-w-7',
	};
	const imgDimensionClasses = {
		sm: 'w-3',
		md: 'w-4',
	};

	const paddingClasses = $derived({
		sm: hasIconOnly || hasImageOnly ? 'p-0.75' : 'py-0.5 px-1',
		md: hasIconOnly || hasImageOnly ? 'p-1.5' : 'py-1 px-2',
	});

	const radiusClasses = {
		sm: 'rd-1',
		md: 'rd-2',
	};

	const sizeClasses = $derived(`${imgUrl ? iconContainerDimensionClasses[size] : ''} ${isSquare ? radiusClasses[size] : 'rd-full'} ${paddingClasses[size]}`);
	const style = $derived(colorStyles && getStyle<BadgeStyle>(colorStyles));
</script>

<div
	class={['flex items-center justify-center w-max shrink-0', sizeClasses, derivedClasses, classes]}
	{style}
>
	{#if children}{@render children()}{:else}
		{#if imgUrl}
			<Image src={imgUrl} alt={text || ''} class={imgDimensionClasses[size]} />
		{:else}
			<IconLabel
				{icon}
				label={text}
				hideLabelOnMobile={hideTextOnMobile}
				{iconPosition}
				{amountOfRepeatedIcons}
				{iconClasses}
				{labelClasses}
			/>
		{/if}
	{/if}
</div>

<style>
	.dot {
		--uno: relative before:content-[''] before:block before:absolute before:z-10 before:top--1 before:right--1 before:w-2.5 before:h-2.5 before:rounded-full;
	}
	.dot-green {
		--uno:  before:bg-green;
	}
	.dot-orange {
		--uno:  before:bg-warning;
	}
	.dot-red {
		--uno: before:bg-error;
	}
</style>
